if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.requestId = 0;
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = this.querySelector('[name="filter.p.product_type"]');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
      clearTimeout(this.statusTimer);
      clearTimeout(this.focusTimer);
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', () => {
        clearTimeout(this.inputTimer);
        this.requestId += 1;
        this.controller?.abort();
        this.removeAttribute('results');
        this.clearSelection();
        if (!this.getQuery()) this.close();
        else this.inputTimer = setTimeout(() => this.handleInput(), 200);
      });
      this.resetBtn.addEventListener('click', () => {
        this.input.value = '';
        this.input.setAttribute('value', '');
        this.removeAttribute('results');
        this.close();
        this.input.focus();
      });
      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.addEventListener('focusout', (evt) => {
        if (evt.relatedTarget && !this.contains(evt.relatedTarget)) this.close();
        else {
          clearTimeout(this.focusTimer);
          this.focusTimer = setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0);
        }
      });
      this.results.addEventListener('click', (evt) => {
        if (evt.target.closest('.js-search-close')) {
          this.input.focus();
          this.close();
        }
        if (evt.target.closest('.js-search-view-all, [role="tab"]')) this.clearSelection();
      });
      this.results.addEventListener('keydown', (evt) => {
        if (evt.target.matches('[role="tab"]')) this.clearSelection();
      });
      this.overlay.addEventListener('click', () => this.close());
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this), true);
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (!this.getQuery()) evt.preventDefault();
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      if (evt.key === 'Escape') {
        if (this.hasAttribute('open')) {
          evt.preventDefault();
          this.input.focus();
        }
        this.close();
        return;
      }
      if (evt.target !== this.input || !this.hasAttribute('open')) return;
      if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown') {
        evt.preventDefault();
        this.handleResultsNav(evt.key);
      } else if (evt.key === 'Enter') {
        const selected = this.results.querySelector('.predictive-search__item[aria-selected="true"] .js-search-link');
        if (selected) {
          evt.preventDefault();
          selected.click();
        }
      }
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    clearSelection() {
      this.results.querySelectorAll('.predictive-search__item[aria-selected="true"]').forEach((item) => item.setAttribute('aria-selected', 'false'));
      this.input.removeAttribute('aria-activedescendant');
    }

    handleResultsNav(key) {
      const items = [...this.results.querySelectorAll('.predictive-search__item')].filter((item) => !item.closest('[hidden]'));
      if (!items.length) return;
      const index = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
      const next = index < 0 ? (key === 'ArrowUp' ? items.length - 1 : 0) : (index + (key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
      this.clearSelection();
      const item = items[next];
      item.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', item.id);
      this.input.setAttribute('aria-controls', item.closest('[role="listbox"]').id);
      this.input.setAttribute('aria-owns', item.closest('[role="listbox"]').id);
      item.scrollIntoView({ block: 'nearest' });
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      const requestId = ++this.requestId;
      this.controller?.abort();
      this.controller = new AbortController();
      this.setLiveRegionLoadingState();
      this.results.setAttribute('aria-busy', 'true');

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      // Expand the query with configured synonyms and dimension format variants.
      let query = searchTerm;
      if (window.SearchTuning) {
        const expanded = window.SearchTuning.expandQuery(searchTerm);
        if (expanded !== searchTerm) query = `(${expanded})`;
      }

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(query)}`;
      } else {
        searchParams = `q=${encodeURIComponent(query)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;
        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        if (error.name !== 'AbortError' && requestId === this.requestId) this.close();
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.clearSelection();
      this.results.innerHTML = resultsMarkup;
      this.results.removeAttribute('aria-busy');
      this.results.querySelectorAll('[role="listbox"]').forEach((list, index) => { list.id = this.input.id + '-list-' + index; });
      this.results.querySelectorAll('.predictive-search__item').forEach((item, index) => {
        item.id = this.input.id + '-option-' + index;
        item.setAttribute('aria-selected', 'false');
      });
      const list = [...this.results.querySelectorAll('[role="listbox"]')].find((item) => !item.closest('[hidden]'));
      this.input.setAttribute('aria-controls', list ? list.id : this.input.id + '-results');
      this.input.setAttribute('aria-owns', list ? list.id : this.input.id + '-results');
      this.setLiveRegionText(this.dataset.resultsText || '');
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      this.viewportHandler = this.viewportHandler || (() => {
        this.style.setProperty('--search-viewport-height', (window.visualViewport?.height || window.innerHeight) + 'px');
        this.style.setProperty('--search-viewport-top', (window.visualViewport?.offsetTop || 0) + 'px');
      });
      this.viewportHandler();
      window.visualViewport?.addEventListener('resize', this.viewportHandler);
      window.visualViewport?.addEventListener('scroll', this.viewportHandler);
      window.addEventListener('resize', this.viewportHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close() {
      clearTimeout(this.inputTimer);
      this.requestId += 1;
      this.controller?.abort();
      this.clearSelection();
      this.removeAttribute('open');
      this.results.removeAttribute('aria-busy');
      this.resetBtn.hidden = !this.getQuery();
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');
      window.visualViewport?.removeEventListener('resize', this.viewportHandler);
      window.visualViewport?.removeEventListener('scroll', this.viewportHandler);
      window.removeEventListener('resize', this.viewportHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
